'use client';
import {
  Badge,
  Button,
  Collapse,
  Divider,
  Grid,
  IconButton,
  Table as MUITable,
  Pagination,
  Stack,
  TableBody,
  TableCell,
  TableCellProps,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Tooltip,
  Typography,
} from '@mui/material';
import { dateToTimestamp } from '@repo/utils';
import { useRouter, useSearchParams } from 'next/navigation';
import { useEffect, useRef, useState, useTransition } from 'react';
import { Doodles } from '../../assets';
import { COLORS } from '../../constants';
import { MaterialIcon, type MaterialIconType } from '../materialIcon';
import { Filters } from './filter';

export type SortType = 'date' | 'number' | 'alphabetic';

export type CellType = {
  label: string;
  showLabelMobile?: boolean;
  sort?: SortType;
  name?: string;
} & TableCellProps;

export type TableHeadersType = CellType[];

export type RowDataType = {
  name?: string;
  rawValue?: string;
  value: string | number | React.JSX.Element;
  align?: TableCellProps['align'];
};

export type TableDataType = {
  rowData: RowDataType[];
  rowRedirect?: string;
  rowAction?: () => void;
  dropdownContent?: React.JSX.Element;
};

export type TableActions = {
  onClick: () => void;
  label: string;
  icon: MaterialIconType;
  active?: boolean;
  badge?: number;
};

export type filterField = {
  name: string;
  label: string;
  type?: 'text' | 'number' | 'date' | 'dateRange' | 'select' | 'selectMultiple'; // @todo: 'boolean';
  length?: number;
  format?: 'cuit' | 'phone' | 'currency';
  placeholder?: string;
  startIcon?: {
    icon: MaterialIconType;
    color?: string;
  };
  options?: { label: string; value: string }[];
  dynamicOptions?: {
    leadFilter: string;
    relation: Record<string, string[]>;
  };
  dateNames?: [string, string];
};

export type TableProps = {
  title?: string;
  titleData?: string;
  iconTitle?: MaterialIconType;
  subtitle?: string;
  actions?: TableActions[];
  headers: TableHeadersType;
  showHeadersMobile?: boolean;
  filters?: filterField[];
  tableData: TableDataType[];
  rows?: number;
  flex?: boolean;
  dropdown?: boolean;
  clickableRows?: boolean;
  hideActions?: boolean;
  hidePagination?: boolean;
  downloadComponent?: React.JSX.Element;
  isServerSideFilter?: boolean;
  serverPage?: string;
  totalElements?: number;
};

/**
 * A component that renders a table with filters and pagination.
 *
 * The table will render a set of headers, and a set of rows. Each row will
 * contain a set of cells, with the value of each cell being the result of
 * `rowData[index].value`. If the value is an object, the object will be
 * rendered directly in the cell.
 *
 * The component will also render a set of filters above the table, and a
 * pagination component below the table. The filters will be rendered as a
 * set of text fields, with the label of each filter being the value of the
 * `label` property of the corresponding `filterField` object.
 *
 * The pagination component will render a set of buttons that allow the user
 * to navigate through the table. The buttons will be labeled with the page
 * number that they correspond to.
 *
 * The table will be rendered with a flexbox layout, with the headers and rows
 * being rendered as flex items. The flex items will be arranged in a row, with
 * the headers being rendered above the rows.
 *
 * The table will be rendered with a set of styles that make it look like a
 * material-ui table.
 *
 * @prop {string} title - The title of the table.
 *
 * @prop {string} titleData - Extra data for the title of the table.
 *
 * @prop {MaterialIconType} iconTitle - The icon title of the table.
 *
 * @prop {string} subtitle - The subtitle of the table.
 *
 * @prop {filterField[]} filters - An array of objects that define the filters
 * that will be rendered above the table.
 *
 * * `[date_from]` and `[date_to]` are used to filter date ranges
 *
 * * `|` will be interpreted as OR
 *
 * @prop {CellType[]} headers - An array of objects that define the headers of
 * the table.
 *
 * @prop {boolean} showHeadersMobile - A boolean that indicates whether the headers should be shown on mobile.
 *
 * @prop {TableDataType[]} tableData - An array of objects that define the
 * data that will be rendered in the table.
 *
 * @prop {number} rows - The number of rows that will be rendered in the table.
 *
 * @prop {boolean} flex - A boolean that indicates whether the rows variant
 * should be set to mobileFlex instead of the base table-row display.
 *
 * @prop {boolean} dropdown - A boolean that indicates whether the rows
 * should be a dropdown.
 *
 * @prop {boolean} clickableRows - A boolean that indicates whether the rows
 * should be clickable.
 *
 * @prop {boolean} hideActions - A boolean that indicates whether the
 * actions should be hidden.
 *
 * @prop {boolean} hidePagination - A boolean that indicates whether the
 * pagination component should be hidden.
 *
 * @prop {string} downloadAction - Action to download table CSV.
 *
 * @prop {downloadComponent} downloadComponent - Component to download table CSV.
 *
 * @prop {boolean} isServerSideFilter - If true, the table will not filter data on the client side.
 *
 * @prop {string} serverPage - The offset value from the server params.
 *
 * @returns {React.JSX.Element} - The rendered table component.
 */
export const Table = ({
  title,
  titleData,
  iconTitle,
  subtitle,
  filters,
  headers,
  showHeadersMobile,
  tableData,
  rows,
  flex,
  dropdown,
  clickableRows,
  hideActions,
  hidePagination,
  downloadComponent,
  isServerSideFilter = false,
  serverPage,
  totalElements,
}: TableProps) => {
  const router = useRouter();
  const [isPending, startTransition] = useTransition();
  const [tablePage, setTablePage] = useState(0);
  const [showFilters, setShowFilters] = useState(false);
  const tableRef = useRef<HTMLDivElement | null>(null);
  const searchParams = useSearchParams();
  const pageSize = rows || 10;

  const [filteredData, setFilteredData] = useState<TableDataType[]>(tableData || []);

  const [activeSort, setActiveSort] = useState<number>(-1);
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');

  const [filterSize, setFilterSize] = useState<number>(0);
  const [serverFilterTrigger, setServerFilterTrigger] = useState<number>(0);

  useEffect(() => {
    let filteredSearchParams = new URLSearchParams(Array.from(searchParams.entries()));
    filteredSearchParams.delete('sortIndex');
    filteredSearchParams.delete('sortDirection');
    filteredSearchParams.delete('sort');
    filteredSearchParams.delete('page');
    filteredSearchParams.delete('offset');
    filteredSearchParams.delete('limit');
    setFilterSize(filteredSearchParams.size);
    if (isServerSideFilter) {
      setFilteredData(tableData);
      setServerFilterTrigger((prev) => prev + 1);
      return;
    }
    if (searchParams) {
      let filtered = [...(tableData || [])];
      if (filteredSearchParams.size > 0)
        filteredSearchParams.forEach((param, key) => {
          if (key.includes('[date_from]')) {
            filtered = filtered.filter(({ rowData }) =>
              rowData.some(
                ({ name, rawValue }) =>
                  name === key.replace('[date_from]', '') &&
                  new Date(`${rawValue?.split(' ')[0]}`) >= new Date(`${param}`),
              ),
            );
          } else if (key.includes('[date_to]')) {
            filtered = filtered.filter(({ rowData }) =>
              rowData.some(
                ({ rawValue, name }) =>
                  name === key.replace('[date_to]', '') &&
                  new Date(`${rawValue?.split(' ')[0]}`) <= new Date(`${param}`),
              ),
            );
          } else {
            filtered = filtered.filter(({ rowData }) =>
              rowData.some(({ rawValue, name, value }) => {
                const validValue = `${rawValue || value}`;
                return (
                  name === key &&
                  param?.split(',').some((_value) => validValue.toLowerCase().includes(_value.toLowerCase()))
                );
              }),
            );
          }
        });
      if (searchParams.get('sortIndex') && searchParams.get('sortDirection')) {
        const sortDirection = searchParams.get('sortDirection') === 'asc' ? 'asc' : 'desc';
        const sortIndex = parseInt(searchParams.get('sortIndex') || '0');
        const sortType = headers[sortIndex]?.sort;
        setActiveSort(sortIndex);
        setSortDirection(sortDirection);
        const decodeValue = (value: string) => (sortType === 'date' ? dateToTimestamp(value) : parseFloat(value));
        filtered.sort((a, b) => {
          const aValue = sortDirection === 'asc' ? a.rowData[sortIndex]?.rawValue : b.rowData[sortIndex]?.rawValue;
          const bValue = sortDirection === 'asc' ? b.rowData[sortIndex]?.rawValue : a.rowData[sortIndex]?.rawValue;
          if (sortType === 'alphabetic') {
            return aValue?.toLocaleLowerCase()?.localeCompare(bValue?.toLocaleLowerCase() || '') || 0;
          } else {
            return decodeValue(aValue || '0') - decodeValue(bValue || '0');
          }
        });
      }
      setFilteredData(filtered);
    }
  }, [searchParams, filters, tableData, showFilters]);

  const sortList = (i: number) => {
    const current = new URLSearchParams(searchParams.toString());
    if (!isServerSideFilter) {
      const currentDirection = current.get('sortDirection');
      const currentIndex = current.get('sortIndex');
      if (currentIndex !== `${i}`) {
        current.set('sortDirection', 'asc');
        current.set('sortIndex', `${i}`);
        setActiveSort(i);
      } else {
        if (currentDirection === 'asc') {
          current.set('sortDirection', 'desc');
        } else if (currentDirection === 'desc') {
          setActiveSort(-1);
          current.delete('sortDirection');
          current.delete('sortIndex');
        } else {
          current.set('sortDirection', 'asc');
        }
      }
      if (current.size || searchParams.toString() !== current.toString()) {
        window.history.replaceState(null, '', `?${current.toString()}`);
      }
    } else {
      const currentSort = current.get('sort');
      const headerName = headers[i]?.name?.toUpperCase();
      let nextDirection: 'ASC' | 'DESC' | '' = 'ASC';
      if (currentSort && activeSort === i) {
        const [, dir] = currentSort.split(',');
        nextDirection = dir === 'ASC' ? 'DESC' : dir === 'DESC' ? '' : 'ASC';
      } else {
        nextDirection = 'ASC';
      }
      current.delete('sort');
      if (headerName && nextDirection) {
        current.set('sort', `${headerName},${nextDirection}`);
        setSortDirection(nextDirection.toLowerCase() as 'asc' | 'desc');
        setActiveSort(i);
      } else {
        setActiveSort(-1);
        setSortDirection('asc');
      }
      startTransition(() => {
        router.push(`?${current.toString()}`);
      });
    }
  };

  const handlePagination = (nextPage: number) => {
    if (isServerSideFilter) {
      const current = new URLSearchParams(searchParams.toString());
      current.set('page', `${nextPage}`);
      current.set('offset', `${(nextPage - 1) * pageSize}`);
      current.set('limit', `${pageSize}`);
      startTransition(() => {
        router.push(`?${current}`);
        if (tableRef.current) {
          tableRef.current.scrollTo({ top: 0, behavior: 'smooth' });
        }
      });
    } else {
      setTablePage(nextPage - 1);
    }
  };

  useEffect(() => {
    const maxPage = Math.floor(filteredData.length / pageSize);
    if (!isServerSideFilter && tablePage > maxPage) {
      setTablePage(0);
    }
  }, [filteredData, tablePage, pageSize]);

  useEffect(() => {
    if (!isServerSideFilter && searchParams.get('page')) {
      setTablePage(parseInt(searchParams.get('page') || '1') - 1);
    }
  }, []);

  useEffect(() => {
    const current = new URLSearchParams(Array.from(searchParams.entries()));
    if (!isServerSideFilter) {
      if (tablePage) {
        current.set('offset', `${tablePage * pageSize}`);
        current.set('limit', `${pageSize}`);
      } else {
        current.delete('offset');
        current.delete('limit');
      }
      if (current.size || searchParams.toString() !== current.toString()) {
        window.history.replaceState(null, '', `?${current.toString()}`);
      }
    }
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [tablePage]);

  return (
    <Grid container wrap="nowrap" sx={{ width: '100%', flexDirection: 'column', gap: 2, height: '100%' }}>
      {title || downloadComponent || filters?.length ? (
        <Grid
          container
          sx={{
            flexDirection: 'row',
            gap: 2,
            alignItems: 'center',
            justifyContent: 'space-between',
            px: { xs: 2, md: 0 },
            pt: { xs: 2, md: 0 },
          }}
        >
          {title && (
            <Grid
              sx={{
                display: 'flex',
                flexDirection: 'row',
                gap: 1,
                alignItems: 'center',
                justifyContent: 'flex-start',
                minHeight: '40px',
              }}
            >
              <Typography variant="h1withIcon">
                {iconTitle && (
                  <MaterialIcon icon={iconTitle} sx={{ height: '100%', lineHeight: 1 }} color="secondary" />
                )}
                <span>{title}</span>
              </Typography>
              {titleData && (
                <Typography variant="body1" color="textDisabled" sx={{ display: { xs: 'none', md: 'block' } }}>
                  {titleData}
                </Typography>
              )}
            </Grid>
          )}
          <Grid
            container
            sx={{ flexDirection: 'row', gap: 2, alignItems: 'center', display: hideActions ? 'none' : 'flex' }}
          >
            {downloadComponent && tableData.length ? downloadComponent : null}
            {filters?.length ? (
              <Tooltip title={'Filtros'}>
                <IconButton onClick={() => setShowFilters(!showFilters)} sx={{ mt: -2, mb: -2, mr: -1 }}>
                  <Badge color="error" badgeContent={filterSize} invisible={!filterSize}>
                    <MaterialIcon
                      icon={'filter_list'}
                      sx={{ color: showFilters ? COLORS.primary.purple.main : COLORS.text.light3 }}
                    />
                  </Badge>
                </IconButton>
              </Tooltip>
            ) : null}
          </Grid>
        </Grid>
      ) : null}
      {subtitle && (
        <Typography sx={{ px: { xs: 2, md: 0 }, pb: { xs: 2, md: 0 }, width: '90%' }} mt={-2} variant="h2">
          <span>{subtitle}</span>
        </Typography>
      )}
      {filters && (
        <Filters
          open={showFilters}
          onClose={() => setShowFilters(false)}
          fields={filters}
          isServerSideFilter={isServerSideFilter}
          serverFilterTrigger={serverFilterTrigger}
        />
      )}
      {filteredData?.length ? (
        <>
          <TableContainer
            ref={tableRef}
            sx={{
              display: { md: 'block' },
              //   overflowX: 'auto',
              //   height: '100%',
            }}
          >
            <MUITable
              id="table-wrapper"
              data-testid="table"
              stickyHeader
              sx={{
                opacity: isPending ? 0.6 : 1,
                filter: isPending ? 'grayscale(100%)' : 'grayscale(0%)',
                pointerEvents: isPending ? 'none' : 'auto',
                transition: 'all 0.3s ease',
              }}
            >
              <TableHead
                data-testid="table-head"
                sx={{ display: { xs: showHeadersMobile ? 'table-header-group' : 'none', md: 'table-header-group' } }}
              >
                <TableRow>
                  {headers && headers?.length ? (
                    headers?.map(({ label, align, sort, ...headersRest }, i) => (
                      <TableCell key={label} align={align} {...headersRest}>
                        {sort ? (
                          <TableSortLabel
                            active={activeSort === i}
                            direction={activeSort === i ? sortDirection : 'asc'}
                            sx={{
                              fontWeight: 400,
                              '& .MuiTableSortLabel-icon': {
                                color: `${COLORS.primary.purple.main} !important`,
                              },
                            }}
                            onClick={() => sortList(i)}
                          >
                            {label}
                          </TableSortLabel>
                        ) : (
                          <Typography variant="body2" color="textSecondary" alignItems="center" gap={0.5}>
                            {label}
                          </Typography>
                        )}
                      </TableCell>
                    ))
                  ) : (
                    <TableCell />
                  )}
                </TableRow>
              </TableHead>
              <TableBody data-testid="table-body">
                {tableData?.length ? (
                  filteredData
                    ?.slice(tablePage * pageSize, tablePage * pageSize + pageSize)
                    ?.map(({ rowData, rowRedirect, rowAction, dropdownContent }, index) =>
                      dropdown ? (
                        <DropdownRow key={`dropdown-row-${index}`} dropdownContent={dropdownContent}>
                          {rowData?.slice(0, headers.length - 1)?.map(({ value, align }, cellIndex) => (
                            <TableCell
                              key={`dropdown-cell-${index}-${cellIndex}`}
                              align={align as TableCellProps['align']}
                              sx={{ flexWrap: { xs: 'wrap', md: 'unset' } }}
                            >
                              {value && typeof value === 'object' ? (
                                value
                              ) : (
                                <Typography
                                  variant="body2"
                                  sx={{ color: { xs: 'text.primary', md: COLORS.text.light3 } }}
                                >
                                  {value ?? '-'}
                                </Typography>
                              )}
                            </TableCell>
                          ))}
                        </DropdownRow>
                      ) : (
                        <TableRow
                          variant={flex ? 'mobileFlex' : ''}
                          key={`table-row-${index}`}
                          hover={clickableRows}
                          sx={{ cursor: clickableRows ? 'pointer' : 'default' }}
                          onClick={() => (rowAction ? rowAction() : rowRedirect ? router.push(rowRedirect) : undefined)}
                        >
                          {headers?.length &&
                            rowData?.map(({ value, align }, cellIndex) => (
                              <TableCell key={`cell-${index}-${cellIndex}`} align={align as TableCellProps['align']}>
                                <>
                                  {headers[cellIndex]?.showLabelMobile && headers[cellIndex]?.label && (
                                    <Typography
                                      variant="body3"
                                      color="textSecondary"
                                      sx={{ display: { xs: 'block', md: 'none' } }}
                                    >
                                      <span>{headers[cellIndex]?.label}:</span>
                                    </Typography>
                                  )}
                                  {value && typeof value === 'object' ? (
                                    value
                                  ) : (
                                    <Typography
                                      variant="body2"
                                      sx={{ color: { xs: 'text.primary', md: COLORS.text.light3 } }}
                                    >
                                      <span>{value ?? '-'}</span>
                                    </Typography>
                                  )}
                                </>
                              </TableCell>
                            ))}
                        </TableRow>
                      ),
                    )
                ) : (
                  <TableRow />
                )}
              </TableBody>
            </MUITable>
          </TableContainer>
          {!hidePagination &&
            Math.ceil(isServerSideFilter && totalElements ? totalElements / pageSize : filteredData.length / pageSize) >
              1 && (
              <Pagination
                count={
                  isServerSideFilter && totalElements
                    ? Math.ceil(totalElements / pageSize)
                    : Math.ceil(filteredData.length / pageSize)
                }
                page={isServerSideFilter ? Number(serverPage) || 1 : tablePage + 1}
                size="large"
                boundaryCount={3}
                shape="rounded"
                disabled={isPending}
                onChange={(e, page) => handlePagination(page)}
              />
            )}
        </>
      ) : (
        <Grid
          container
          justifyContent={'center'}
          alignItems={'center'}
          direction={'column'}
          gap={2}
          p={4}
          sx={{ width: '100%', flex: 1 }}
        >
          <Doodles type="end" />
          <Typography variant="body3">Ups, no encontramos resultados.</Typography>
        </Grid>
      )}
    </Grid>
  );
};

const DropdownRow = ({
  dropdownContent,
  children,
}: {
  dropdownContent?: React.JSX.Element;
  children: React.ReactNode;
}) => {
  const [open, setOpen] = useState(false);
  const searchParams = useSearchParams();
  useEffect(() => {
    setOpen(false);
  }, [searchParams.toString()]);
  return (
    <>
      <TableRow variant="mobileFlex">
        {children}
        <TableCell sx={{ display: { xs: 'table-cell', md: 'none !important' } }}>
          <Collapse in={open} timeout="auto" unmountOnExit sx={{ width: '-webkit-fill-available' }}>
            <Grid
              sx={{
                // p: 2,
                // mb: { xs: 2, md: 0 },
                // mt: { xs: -3, md: 0 },
                // borderRadius: { xs: 2, md: 0 },
                // borderTopLeftRadius: { xs: 2, md: 0 },
                // borderTopRightRadius: { xs: 2, md: 0 },
                // border: '1px solid #E9DCFF',
                borderTop: 'none',
                // backgroundColor: COLORS.background[0],
              }}
            >
              {dropdownContent}
            </Grid>
          </Collapse>
        </TableCell>
        <TableCell>
          <IconButton
            sx={{ display: { xs: 'none', md: 'flex' } }}
            aria-label="expand row"
            size="small"
            onClick={() => setOpen(!open)}
          >
            <MaterialIcon
              icon="keyboard_arrow_down"
              color="secondary"
              sx={{
                transform: open ? 'rotate(-180deg)' : 'rotate(0)',
                transition: '0.2s ease',
              }}
            />
          </IconButton>
          <Stack width="100%" gap={1} sx={{ display: { xs: 'flex', md: 'none' }, pt: { xs: 1, md: 0 } }}>
            <Divider flexItem orientation="horizontal" />
            <Button
              aria-label="expand row button"
              fullWidth
              variant="text"
              sx={{ alignItems: 'center', lineHeight: 0 }}
              onClick={() => setOpen(!open)}
            >
              Ver {open ? 'menos' : 'más'}
              <MaterialIcon
                icon="keyboard_arrow_down"
                color="primary"
                sx={{
                  transform: open ? 'rotate(-180deg)' : 'rotate(0)',
                  transition: '0.2s ease',
                  lineHeight: 1,
                }}
              />
            </Button>
          </Stack>
        </TableCell>
      </TableRow>
      <TableRow
        sx={{
          display: { xs: 'none', md: 'table-row' },
          border: 'none',
        }}
      >
        <TableCell
          sx={{
            py: 0,
            px: { xs: 2, md: 0 },
            borderBottom: { xs: 'none', md: open ? '1px solid rgba(224, 224, 224, 1)' : 'none' },
          }}
          colSpan={12}
        >
          <Collapse in={open} timeout="auto" unmountOnExit>
            <Grid
              sx={{
                p: 2,
                // mb: { xs: 2, md: 0 },
                // mt: { xs: -3, md: 0 },
                // borderRadius: { xs: 2, md: 0 },
                // borderTopLeftRadius: { xs: 2, md: 0 },
                // borderTopRightRadius: { xs: 2, md: 0 },
                // border: '1px solid #E9DCFF',
                borderTop: 'none',
                backgroundColor: COLORS.background[0],
              }}
            >
              {dropdownContent}
            </Grid>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
};
